import React, { useState, useEffect } from "react";
import { Loader, Image, Table, Segment, Grid, Header } from "semantic-ui-react";
import { UserPartner } from "../../../../api";
import { PlanesAPI } from "../../../../api/planesAPI";
import { InstitutionApi } from "../../../../api";
import { useAuth } from "../../../../hooks";

const userPartnerController = new UserPartner();
const planesAPI = new PlanesAPI();
const institutionApi = new InstitutionApi();

export function PartnerProfile({ reload, onReload }) {
  const { user, accessToken } = useAuth();
  const partnerId = user.user?.holded;

  const [partnerContact, setPartnerContact] = useState(null);
  const [loading, setLoading] = useState(true);

  // Activations state
  const [activationsDetails, setActivationsDetails] = useState([]);

  // Institutions state
  const [activeInst, setActiveInst] = useState([]);
  const [inactiveInst, setInactiveInst] = useState([]);
  const [loadingInst, setLoadingInst] = useState(true);

  // Fetch partner contact
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        if (partnerId) {
          const res = await userPartnerController.getPartner(
            accessToken,
            partnerId
          );
          setPartnerContact(res.contact);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, [reload, partnerId, accessToken]);

  // Fetch activations details
  useEffect(() => {
    if (user.user?.planEducaActivations?.length) {
      Promise.all(
        user.user.planEducaActivations.map(async (act) => {
          try {
            const planDetail = await planesAPI.getPlanById(
              accessToken,
              act.plan
            );
            return {
              title: planDetail.plan?.title || act.plan,
              quantity: act.quantity,
            };
          } catch {
            return { title: act.plan, quantity: act.quantity };
          }
        })
      ).then(setActivationsDetails);
    }
  }, [user.user, accessToken]);

  // Fetch institutions lists
  useEffect(() => {
    (async () => {
      setLoadingInst(true);
      try {
        const [actRes, inactRes] = await Promise.all([
          institutionApi.getInstitutions(
            accessToken,
            user.user._id,
            true
          ),
          institutionApi.getInstitutions(
            accessToken,
            user.user._id,
            false
          ),
        ]);
        setActiveInst(actRes.institutions || []);
        setInactiveInst(inactRes.institutions || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingInst(false);
      }
    })();
  }, [reload, accessToken, user.user._id]);

  if (loading) {
    return <Loader active inline="centered" />;
  }

  return (
    <Segment>
      <Grid stackable>
        <Grid.Column width={4} textAlign="center">
          {user.user.avatar && <Image src={user.user.avatar} circular size="small" />}
        </Grid.Column>
        <Grid.Column width={12}>
          <Header as="h2">Perfil de Partner</Header>
          <p>
            <strong>Cliente:</strong> {partnerContact?.name || "–"} ({partnerContact?.code || "–"})
          </p>
          <p>
            <strong>Web:</strong> {partnerContact?.socialNetworks?.website || "–"}
          </p>
          <p>
            <strong>Contacto:</strong> {user.user.firstname} {user.user.lastname}
          </p>
          <p>
            <strong>Email:</strong> {user.user.email}
          </p>
          {user.user.phone && (
            <p>
              <strong>Teléfono:</strong> {user.user.phone}
            </p>
          )}
        </Grid.Column>
      </Grid>

      {/* Active Plans Table */}
      <Segment piled>
        <Header as="h3">Planes Contratados </Header>
        {activationsDetails.length > 0 ? (
          <Table celled striped>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Plan</Table.HeaderCell>
                <Table.HeaderCell>Disponibles</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {activationsDetails.map((act, i) => (
                <Table.Row key={i}>
                  <Table.Cell>{act.title}</Table.Cell>
                  <Table.Cell>{act.quantity}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        ) : (
          <p>No hay planes activos.</p>
        )}
      </Segment>

      {/* Active Institutions */}
      <Segment piled>
        <Header as="h3">Instituciones Activas ({activeInst.length})</Header>
        {loadingInst ? (
          <Loader active inline="centered" />
        ) : activeInst.length > 0 ? (
          <Table celled striped>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Nombre</Table.HeaderCell>
                <Table.HeaderCell>Plan</Table.HeaderCell>
                <Table.HeaderCell>Creada</Table.HeaderCell>
                <Table.HeaderCell>Expira</Table.HeaderCell>
                <Table.HeaderCell>Estudiantes</Table.HeaderCell>
                <Table.HeaderCell>Profesores</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {activeInst.map((inst) => (
                <Table.Row key={inst._id}>
                  <Table.Cell>{inst.institutionnam}</Table.Cell>
                  <Table.Cell>{inst.planEduca?.title}</Table.Cell>
                  <Table.Cell>
                    {new Date(inst.created_at).toLocaleDateString()}
                  </Table.Cell>
                  <Table.Cell>
                    {inst.expired_at
                      ? new Date(inst.expired_at).toLocaleDateString()
                      : "–"}
                  </Table.Cell>
                  <Table.Cell>{inst.studentsnum}</Table.Cell>
                  <Table.Cell>{inst.teachersnum}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        ) : (
          <p>No tiene instituciones activas.</p>
        )}
      </Segment>

      {/* Inactive Institutions */}
      <Segment piled>
        <Header as="h3">Instituciones Inactivas ({inactiveInst.length})</Header>
        {loadingInst ? (
          <Loader active inline="centered" />
        ) : inactiveInst.length > 0 ? (
          <Table celled striped>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Nombre</Table.HeaderCell>
                <Table.HeaderCell>Plan</Table.HeaderCell>
                <Table.HeaderCell>Creada</Table.HeaderCell>
                <Table.HeaderCell>Estudiantes</Table.HeaderCell>
                <Table.HeaderCell>Profesores</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {inactiveInst.map((inst) => (
                <Table.Row key={inst._id}>
                  <Table.Cell>{inst.institutionnam}</Table.Cell>
                  <Table.Cell>{inst.planEduca?.title}</Table.Cell>
                  <Table.Cell>
                    {new Date(inst.created_at).toLocaleDateString()}
                  </Table.Cell>
                  <Table.Cell>{inst.studentsnum}</Table.Cell>
                  <Table.Cell>{inst.teachersnum}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        ) : (
          <p>No tiene instituciones inactivas.</p>
        )}
      </Segment>
    </Segment>
  );
}
